import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useOrders } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { FaQrcode, FaMobile, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const { placeOrder } = useOrders();
  const { user } = useAuth();
  
  const [paymentMethod, setPaymentMethod] = useState('qr');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user) {
    return (
      <div className="container text-center mt-5">
        <h2>Please login to checkout</h2>
        <button onClick={() => navigate('/login')} className="btn btn-primary">
          Login
        </button>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="container text-center mt-5">
        <h2>Your cart is empty</h2>
        <button onClick={() => navigate('/products')} className="btn btn-primary">
          Browse Products
        </button>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          productId: item._id,
          productName: item.name,
          productPrice: item.price,
          productImage: item.image,
          quantity: item.quantity,
          totalPrice: item.price * item.quantity
        })),
        subtotal: getCartTotal(),
        deliveryFee: 50,
        total: getCartTotal() + 50,
        paymentMethod: 'qr',
        deliveryDate: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
        deliveryInstructions: '',
        estimatedDeliveryTime: 'Between 6:00 PM - 8:00 PM',
        notes: ''
      };

      const result = await placeOrder(orderData);
      
      if (result.success) {
        clearCart();
        navigate('/orders', { 
          state: { 
            message: 'Order placed successfully! Please complete the payment via QR code. Your order will be delivered soon.',
            orderId: result.orderId 
          }
        });
      }
    } catch (error) {
      console.error('Order placement failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const subtotal = getCartTotal();
  const deliveryFee = 50;
  const total = subtotal + deliveryFee;

  return (
    <div className="container">
      <h2 className="text-center mb-4">Checkout</h2>
      
      <div className="row">
        <div className="col-md-8">
          <div className="card mb-4">
            <div className="card-body">
              <h4 className="card-title">Delivery Information</h4>
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Name:</strong> {user.name}</p>
                  <p><strong>Phone:</strong> {user.phoneNumber}</p>
                </div>
                <div className="col-md-6">
                  <p><strong>Society:</strong> {user.society}</p>
                  <p><strong>Address:</strong> {user.address}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h4 className="card-title">
                <FaQrcode className="me-2" />
                Payment via QR Code
              </h4>
              
              <div className="text-center mb-4">
                <div className="qr-code-container p-4 bg-light rounded">
                  {/* Replace this with your actual QR code image */}
                  <img 
                    src="https://via.placeholder.com/300x300/007bff/ffffff?text=Your+QR+Code" 
                    alt="Payment QR Code"
                    className="img-fluid mb-3"
                    style={{ maxWidth: '300px', border: '2px solid #007bff', borderRadius: '10px' }}
                  />
                  <p className="text-muted mb-2">
                    <FaMobile className="me-1" />
                    Scan this QR code with your payment app
                  </p>
                </div>
              </div>

              <div className="alert alert-info">
                <h6><FaInfoCircle className="me-2" />Payment Instructions:</h6>
                <ol className="mb-0">
                  <li>Open your preferred payment app (Paytm, Google Pay, PhonePe, etc.)</li>
                  <li>Scan the QR code above</li>
                  <li>Enter the amount: <strong>₹{total.toFixed(2)}</strong></li>
                  <li>Add a note: <strong>"Food Order - {user.name}"</strong></li>
                  <li>Complete the payment</li>
                  <li>Click "Confirm Payment" below after successful payment</li>
                </ol>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="paymentConfirmed"
                      className="form-check-input"
                      checked={paymentConfirmed}
                      onChange={(e) => setPaymentConfirmed(e.target.checked)}
                      required
                    />
                    <label htmlFor="paymentConfirmed" className="form-check-label">
                      I confirm that I have completed the payment of ₹{total.toFixed(2)} via QR code
                    </label>
                  </div>
                </div>

                <div className="d-grid gap-2 mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading || !paymentConfirmed}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Placing Order...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Confirm Payment & Place Order
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigate('/cart')}
                    className="btn btn-outline-secondary"
                    disabled={loading}
                  >
                    Back to Cart
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Order Summary</h4>
              
              {cart.map((item) => (
                <div key={item._id} className="d-flex justify-content-between mb-2">
                  <span>{item.name} x {item.quantity}</span>
                  <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              
              <hr />
              
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal:</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              
              <div className="d-flex justify-content-between mb-2">
                <span>Delivery Fee:</span>
                <span>₹{deliveryFee.toFixed(2)}</span>
              </div>
              
              <hr />
              
              <div className="d-flex justify-content-between">
                <strong>Total:</strong>
                <strong>₹{total.toFixed(2)}</strong>
              </div>

              <div className="alert alert-warning mt-3">
                <small>
                  <strong>Note:</strong> Please complete the payment before confirming your order. 
                  Orders will only be processed after payment confirmation.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 